import { AppointmentSpeciality } from '@avanta/shared/constants/appointments';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { t } from '@avanta/shared/i18n';
import {
  type TableColumnFilterConfig,
  type TableColumnFilterOption,
  type TableFilterOption,
} from '@avanta/shared/types/table';
import { getAppointmentSpecialityLabel } from '@avanta/shared/utils/appointments';
import {
  ENTERPRISE_APPOINTMENT_FILTER,
  ENTERPRISE_APPOINTMENT_STATUS,
  PAST_APPOINTMENT_STATUSES,
  UPCOMING_APPOINTMENT_STATUSES,
} from 'constants/appointments';
import { EMPTY_VALUE } from 'constants/common';
import {
  type AppointmentWithDetails,
  type EnterpriseAppointmentFilter,
  type EnterpriseAppointmentStatus,
} from 'types/appointments';

const isMissingSpeciality = (
  specialityId: number | null | undefined
): specialityId is null | undefined => specialityId === null || specialityId === undefined;

export const getSpecialityLabel = (specialityId: number | null | undefined): string => {
  if (isMissingSpeciality(specialityId)) return EMPTY_VALUE;

  return getAppointmentSpecialityLabel(specialityId);
};

export const getSpecialityColumnFilter = (): TableColumnFilterConfig => ({
  allLabel: t('InsuredPersonsList.filters.all', { ns: LOCIZE_NS.INSURANCE }),
  options: Object.values(AppointmentSpeciality).map(value => ({
    value,
    label: getSpecialityLabel(value),
  })),
});

export const getSpecialityFilterOptions = (
  appointments: AppointmentWithDetails[]
): TableColumnFilterOption[] => {
  const specialityIds = new Set<number>();

  for (const appointment of appointments) {
    if (isMissingSpeciality(appointment.specialitiesBookable)) continue;
    specialityIds.add(appointment.specialitiesBookable);
  }

  return [...specialityIds]
    .toSorted((a, b) => a - b)
    .map(specialityId => ({
      value: specialityId,
      label: getSpecialityLabel(specialityId),
    }));
};

export const getAppointmentFilterOptions = (): TableFilterOption<EnterpriseAppointmentFilter>[] => [
  {
    key: ENTERPRISE_APPOINTMENT_FILTER.UPCOMING,
    label: t('SessionsPage.filters.upcoming', { ns: LOCIZE_NS.INSURANCE }),
  },
  {
    key: ENTERPRISE_APPOINTMENT_FILTER.PAST,
    label: t('SessionsPage.filters.past', { ns: LOCIZE_NS.INSURANCE }),
  },
  {
    key: ENTERPRISE_APPOINTMENT_FILTER.CANCELLED,
    label: t('SessionsPage.filters.cancelled', { ns: LOCIZE_NS.INSURANCE }),
  },
];

export const matchesAppointmentFilter = (
  appointment: { status?: EnterpriseAppointmentStatus },
  filter: EnterpriseAppointmentFilter
): boolean => {
  const { status } = appointment;
  if (!status) return false;
  if (filter === ENTERPRISE_APPOINTMENT_FILTER.UPCOMING) {
    return UPCOMING_APPOINTMENT_STATUSES.has(status);
  }
  if (filter === ENTERPRISE_APPOINTMENT_FILTER.PAST) {
    return PAST_APPOINTMENT_STATUSES.has(status);
  }
  return status === filter;
};

const getSessionStatusFilterOptions = (): {
  status: EnterpriseAppointmentStatus;
  label: string;
}[] => [
  {
    status: ENTERPRISE_APPOINTMENT_STATUS.PENDING,
    label: t('AppointmentCard.states.pending', { ns: LOCIZE_NS.APPOINTMENTS }),
  },
  {
    status: ENTERPRISE_APPOINTMENT_STATUS.SCHEDULED,
    label: t('AppointmentCard.states.scheduled', { ns: LOCIZE_NS.APPOINTMENTS }),
  },
  {
    status: ENTERPRISE_APPOINTMENT_STATUS.CONFIRMED,
    label: t('AppointmentCard.states.confirmed', { ns: LOCIZE_NS.APPOINTMENTS }),
  },
  {
    status: ENTERPRISE_APPOINTMENT_STATUS.DONE,
    label: t('AppointmentCard.states.done', { ns: LOCIZE_NS.APPOINTMENTS }),
  },
  {
    status: ENTERPRISE_APPOINTMENT_STATUS.COMPLETED,
    label: t('AppointmentCard.states.completed', { ns: LOCIZE_NS.APPOINTMENTS }),
  },
  {
    status: ENTERPRISE_APPOINTMENT_STATUS.CANCELLED,
    label: t('AppointmentCard.states.canceled', { ns: LOCIZE_NS.APPOINTMENTS }),
  },
];

/** Status column filter. When `allowedStatuses` is provided, only those statuses are offered. */
export const getSessionStatusColumnFilter = (
  allowedStatuses?: ReadonlySet<EnterpriseAppointmentStatus>
): TableColumnFilterConfig => ({
  allLabel: t('InsuredPersonsList.filters.all', { ns: LOCIZE_NS.INSURANCE }),
  options: getSessionStatusFilterOptions()
    .filter(option => !allowedStatuses || allowedStatuses.has(option.status))
    .map(option => ({ value: option.status, label: option.label })),
});
